import PolicyLayout from './PolicyLayout';
import { ADDRESS, COMPANY_NAME, SITE_DOMAIN, SUPPORT_EMAIL } from '../constants/site';

export default function PrivacyPolicy() {
  return (
    <PolicyLayout
      title="Privacy Policy"
      updated="1 August 2026"
      intro={`This Privacy Policy explains how ${COMPANY_NAME} (${ADDRESS}), operator of ${SITE_DOMAIN}, collects, uses, stores, and protects personal information when you browse our website, contact our support desk, or purchase a software licence through us. By using this website you agree to the practices described below.`}
    >
      <h2>Information We Collect</h2>
      <p>
        We only collect information that helps us complete your order and provide technical support.
        Depending on how you interact with us, this may include:
      </p>
      <ul>
        <li>
          <strong>Contact details</strong> — your name, email address, and telephone number when you
          call us, submit a form, or subscribe to our security briefing.
        </li>
        <li>
          <strong>Order information</strong> — the products purchased, licence keys issued, billing
          address, and transaction reference. We never store full payment card numbers.
        </li>
        <li>
          <strong>Support records</strong> — notes taken during a support session, including the
          operating system and software version involved, so we can follow up accurately.
        </li>
        <li>
          <strong>Technical data</strong> — IP address, browser type, device category, referring
          page, and general location, collected automatically for security and analytics.
        </li>
      </ul>

      <h2>How We Use Your Information</h2>
      <p>Information collected through this website is used strictly to:</p>
      <ul>
        <li>Process your order and deliver activation keys and download links.</li>
        <li>Provide installation assistance and ongoing technical support.</li>
        <li>Send transactional messages such as receipts, renewal reminders, and service notices.</li>
        <li>Detect and prevent fraudulent transactions, abuse, and security incidents.</li>
        <li>Improve our website content, page performance, and customer experience.</li>
      </ul>
      <p>
        We do not sell, rent, or trade your personal information to third parties for their own
        marketing purposes under any circumstances.
      </p>

      <h2>Payment Processing</h2>
      <p>
        All payments are handled by PCI-DSS compliant third-party payment processors. Card details
        are transmitted directly to the processor over an encrypted connection and are never stored
        on our servers. We receive only a confirmation of the transaction, the last four digits of
        the card, and the billing name.
      </p>

      <h2>Cookies and Tracking</h2>
      <p>
        We use a small number of cookies to keep the site functioning correctly, remember your
        preferences, and understand which pages are most useful. Analytics cookies are aggregated
        and do not identify you personally. You can block or delete cookies in your browser
        settings, though some parts of the site may not work as intended afterwards.
      </p>

      <h2>Data Sharing</h2>
      <p>We share information only in these limited situations:</p>
      <ul>
        <li>
          With authorised software distributors, solely to issue and validate the licence key you
          purchased.
        </li>
        <li>
          With service providers who host our website, process payments, or deliver email on our
          behalf, all bound by confidentiality obligations.
        </li>
        <li>
          Where required by law, court order, or a legitimate request from a regulatory or law
          enforcement authority.
        </li>
      </ul>

      <h2>Data Retention</h2>
      <p>
        Order and licence records are retained for seven years to satisfy tax, accounting, and
        warranty obligations. Support notes are kept for two years. Marketing contact details are
        removed as soon as you unsubscribe. Technical logs are rotated and deleted within twelve
        months.
      </p>

      <h2>Data Security</h2>
      <p>
        We use TLS encryption across the entire website, restrict internal access to customer
        records on a need-to-know basis, and review our systems regularly. While no method of
        transmission over the internet is completely secure, we take reasonable and appropriate
        measures to protect the information you provide.
      </p>

      <h2>Your Rights</h2>
      <p>
        Depending on where you live, you may have the right to request a copy of the personal data
        we hold about you, correct inaccurate information, request deletion, restrict or object to
        certain processing, and withdraw consent for marketing communications. To exercise any of
        these rights, email <strong>{SUPPORT_EMAIL}</strong> from the address associated with your
        order. We verify identity before releasing records and respond within thirty days.
      </p>

      <h2>Children&apos;s Privacy</h2>
      <p>
        Our services are intended for adults. We do not knowingly collect personal information from
        anyone under the age of sixteen. If you believe a child has provided us with personal data,
        contact us and we will delete the record promptly.
      </p>

      <h2>Changes to This Policy</h2>
      <p>
        We may update this policy to reflect changes in law, technology, or our business practices.
        The revision date at the top of this page always indicates the current version. Material
        changes will be highlighted on our homepage for at least thirty days.
      </p>
    </PolicyLayout>
  );
}
